import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Trash2, Plus, Minus, ShoppingBag, ArrowRight } from 'lucide-react';
import { toast } from 'sonner';

const Cart = () => {
  const [cart, setCart] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    // Charger le panier depuis localStorage
    const savedCart = JSON.parse(localStorage.getItem('cart') || '[]');
    setCart(savedCart);
  }, []);

  const updateCart = (newCart) => {
    setCart(newCart);
    localStorage.setItem('cart', JSON.stringify(newCart));
  };

  const updateQuantity = (productId, change) => {
    const newCart = cart.map(item => {
      if (item.id === productId) {
        const newQuantity = Math.max(1, item.quantity + change);
        return { ...item, quantity: newQuantity };
      }
      return item;
    });
    updateCart(newCart);
  };

  const removeItem = (productId) => {
    const newCart = cart.filter(item => item.id !== productId);
    updateCart(newCart);
    toast.success('Produit retiré du panier');
  };

  const total = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);

  const handleCheckout = () => {
    if (cart.length === 0) {
      toast.error('Votre panier est vide');
      return;
    }
    navigate('/checkout');
  };

  if (cart.length === 0) {
    return (
      <div style={{ 
        minHeight: '60vh', 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center',
        padding: '40px 20px'
      }}>
        <ShoppingBag size={64} style={{ color: '#C9A961', marginBottom: '24px' }} />
        <h1 style={{ fontSize: '32px', marginBottom: '16px' }}>Votre panier est vide</h1>
        <p style={{ color: 'var(--text-secondary)', marginBottom: '32px' }}>
          Découvrez nos produits et commencez vos achats
        </p>
        <Link 
          to="/shop"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            padding: '14px 32px',
            background: '#C9A961',
            color: 'white',
            textDecoration: 'none',
            borderRadius: '8px',
            fontWeight: 600
          }}
        >
          Voir la boutique
          <ArrowRight size={20} />
        </Link>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '80px 20px 40px' }}>
      <h1 style={{ fontSize: '40px', marginBottom: '40px' }}>Mon Panier</h1>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 400px', gap: '40px' }}>
        {/* Liste des produits */}
        <div>
          {cart.map(item => (
            <div 
              key={item.id}
              style={{
                display: 'flex',
                gap: '20px',
                padding: '24px',
                background: 'white',
                borderRadius: '12px',
                marginBottom: '16px',
                border: '1px solid var(--border-light)'
              }}
            >
              {item.image && (
                <img 
                  src={item.image} 
                  alt={item.name}
                  style={{
                    width: '120px',
                    height: '120px',
                    objectFit: 'cover',
                    borderRadius: '8px'
                  }}
                />
              )}
              
              <div style={{ flex: 1 }}>
                <h3 style={{ fontSize: '20px', marginBottom: '8px' }}>{item.name}</h3>
                <p style={{ color: 'var(--text-secondary)', fontSize: '14px', marginBottom: '16px' }}>
                  {item.description}
                </p>
                
                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                  {/* Contrôles quantité */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <button
                      onClick={() => updateQuantity(item.id, -1)}
                      style={{
                        width: '32px',
                        height: '32px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '1px solid var(--border-medium)',
                        borderRadius: '6px',
                        background: 'white',
                        cursor: 'pointer'
                      }}
                    >
                      <Minus size={16} />
                    </button>
                    <span style={{ fontSize: '16px', fontWeight: 600, minWidth: '30px', textAlign: 'center' }}>
                      {item.quantity}
                    </span>
                    <button
                      onClick={() => updateQuantity(item.id, 1)}
                      style={{
                        width: '32px',
                        height: '32px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '1px solid var(--border-medium)',
                        borderRadius: '6px',
                        background: 'white',
                        cursor: 'pointer'
                      }}
                    >
                      <Plus size={16} />
                    </button>
                  </div>

                  <div style={{ flex: 1 }} />

                  {/* Prix */}
                  <span style={{ fontSize: '20px', fontWeight: 600, color: '#C9A961' }}>
                    {(item.price * item.quantity).toFixed(2)}€
                  </span>

                  {/* Supprimer */}
                  <button
                    onClick={() => removeItem(item.id)}
                    style={{
                      padding: '8px',
                      border: 'none',
                      background: '#fee2e2',
                      color: '#dc2626',
                      borderRadius: '6px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center'
                    }}
                  >
                    <Trash2 size={18} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Résumé de la commande */}
        <div>
          <div style={{
            position: 'sticky',
            top: '100px',
            padding: '32px',
            background: 'white',
            borderRadius: '12px',
            border: '1px solid var(--border-light)'
          }}>
            <h2 style={{ fontSize: '24px', marginBottom: '24px' }}>Résumé</h2>
            
            <div style={{ marginBottom: '24px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                <span>Sous-total</span>
                <span>{total.toFixed(2)}€</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '12px' }}>
                <span>Livraison</span>
                <span style={{ color: '#10b981' }}>Gratuite</span>
              </div>
              <div style={{ 
                borderTop: '1px solid var(--border-light)', 
                paddingTop: '12px',
                marginTop: '12px'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '20px', fontWeight: 600 }}>
                  <span>Total</span>
                  <span style={{ color: '#C9A961' }}>{total.toFixed(2)}€</span>
                </div>
              </div>
            </div>

            <button
              onClick={handleCheckout}
              style={{
                width: '100%',
                padding: '16px',
                background: '#C9A961',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: 600,
                cursor: 'pointer',
                marginBottom: '16px'
              }}
            >
              Procéder au paiement
            </button>

            <Link
              to="/shop"
              style={{
                display: 'block',
                textAlign: 'center',
                color: '#C9A961',
                textDecoration: 'none',
                fontSize: '14px'
              }}
            >
              Continuer mes achats
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;
